import { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@easyflow/ui-frontend/components/ui/card';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@easyflow/ui-frontend/components/ui/dialog';
import { Plus, Pencil, Trash2, Lock, Eye } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import apiClient from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import type { KbMetadataDimension, KbMetadataOption, KbMetadataOptionCreate, KbMetadataOptionUpdate } from '@easyflow/shared';

type KbDimension = KbMetadataDimension;

const DIMENSION_LABEL: Record<KbDimension, string> = {
  language: 'Lingue',
  audience: 'Audience',
  sensitivity: 'Sensitività',
};

const DIMENSION_DESCRIPTION: Record<KbDimension, string> = {
  language: 'Lingue disponibili per i documenti caricati nella Knowledge Base.',
  audience: 'Pubblici di destinazione assegnabili a documenti e URL (multi-selezione).',
  sensitivity: 'Livelli di riservatezza assegnabili (selezione singola).',
};

interface DimensionEditorProps {
  dimension: KbDimension;
  options: KbMetadataOption[];
  /** Solo gli admin tenant possono mutare lo schema (il BE rifiuta con 403 gli altri). */
  canEdit: boolean;
  onMutate: () => Promise<void>;
}

function DimensionEditor({ dimension, options, canEdit, onMutate }: DimensionEditorProps) {
  const { toast } = useToast();
  const [creating, setCreating] = useState<boolean>(false);
  const [editing, setEditing] = useState<KbMetadataOption | null>(null);
  const [draftValue, setDraftValue] = useState<string>('');
  const [draftLabel, setDraftLabel] = useState<string>('');
  const [draftDefault, setDraftDefault] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);

  const resetDraft = (): void => {
    setDraftValue('');
    setDraftLabel('');
    setDraftDefault(false);
    setEditing(null);
    setCreating(false);
  };

  const startCreate = (): void => {
    resetDraft();
    setCreating(true);
  };

  const startEdit = (opt: KbMetadataOption): void => {
    setEditing(opt);
    setDraftValue(opt.value);
    setDraftLabel(opt.label);
    setDraftDefault(opt.is_default);
    setCreating(false);
  };

  const submit = async (): Promise<void> => {
    if (!draftLabel.trim()) {
      toast({ title: 'Errore', description: 'Etichetta richiesta', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      if (editing) {
        const body: KbMetadataOptionUpdate = {
          label: draftLabel.trim(),
          is_default: draftDefault,
        };
        const res = await apiClient.patch<KbMetadataOption>(
          `/kb-metadata-schema/${editing.id}`,
          body
        );
        if (!res.success) throw new Error(res.error || 'Errore aggiornamento');
      } else {
        if (!draftValue.trim()) {
          toast({ title: 'Errore', description: 'Valore richiesto', variant: 'destructive' });
          setBusy(false);
          return;
        }
        const body: KbMetadataOptionCreate = {
          dimension,
          value: draftValue.trim(),
          label: draftLabel.trim(),
          is_default: draftDefault,
        };
        const res = await apiClient.post<KbMetadataOption>('/kb-metadata-schema', body);
        if (!res.success) throw new Error(res.error || 'Errore creazione');
      }
      await onMutate();
      resetDraft();
      toast({ title: 'Salvato' });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const remove = async (opt: KbMetadataOption): Promise<void> => {
    if (opt.is_system) {
      toast({ title: 'Bloccato', description: 'Le opzioni di sistema non possono essere eliminate.', variant: 'destructive' });
      return;
    }
    if (!window.confirm(`Eliminare l'opzione "${opt.label}"?`)) return;
    try {
      const res = await apiClient.delete<void>(`/kb-metadata-schema/${opt.id}`);
      if (!res.success) throw new Error(res.error || 'Errore eliminazione');
      await onMutate();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    }
  };

  const sorted = [...options].sort((a, b) => a.position - b.position);

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>{DIMENSION_LABEL[dimension]}</CardTitle>
          <p className="text-sm text-muted-foreground mt-1">{DIMENSION_DESCRIPTION[dimension]}</p>
        </div>
        {canEdit && (
          <Button size="sm" onClick={startCreate}>
            <Plus className="w-4 h-4 mr-1" />
            Nuova opzione
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-2">
          {sorted.length === 0 && (
            <p className="text-sm text-muted-foreground italic">Nessuna opzione configurata.</p>
          )}
          {sorted.map((opt) => (
            <div
              key={opt.id}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-md border bg-background"
            >
              <div className="flex items-center gap-2 min-w-0 flex-1">
                <span className="font-mono text-xs text-muted-foreground shrink-0">{opt.value}</span>
                <span className="font-medium truncate">{opt.label}</span>
                {opt.is_default && <Badge variant="secondary">Default</Badge>}
                {opt.is_system && (
                  <Badge variant="outline" className="gap-1">
                    <Lock className="w-3 h-3" />
                    Sistema
                  </Badge>
                )}
              </div>
              {canEdit && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button size="icon" variant="ghost" onClick={() => startEdit(opt)} title="Modifica">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    onClick={() => void remove(opt)}
                    disabled={opt.is_system}
                    title={opt.is_system ? 'Opzione di sistema' : 'Elimina'}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      </CardContent>

      <Dialog open={creating || editing !== null} onOpenChange={(open) => { if (!open) resetDraft(); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing ? `Modifica opzione "${editing.label}"` : `Nuova opzione — ${DIMENSION_LABEL[dimension]}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="value">Valore (chiave tecnica)</Label>
              <Input
                id="value"
                value={draftValue}
                onChange={(e) => setDraftValue(e.target.value)}
                disabled={editing !== null}
                placeholder="es: marketing, public, fr"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Usato come identificatore. Lowercase, senza spazi. Non modificabile dopo la creazione.
              </p>
            </div>
            <div>
              <Label htmlFor="label">Etichetta visibile</Label>
              <Input
                id="label"
                value={draftLabel}
                onChange={(e) => setDraftLabel(e.target.value)}
                placeholder="es: Marketing, Pubblico, Francese"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="default">Imposta come default</Label>
              <Switch id="default" checked={draftDefault} onCheckedChange={setDraftDefault} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={resetDraft} disabled={busy}>
              Annulla
            </Button>
            <Button onClick={() => void submit()} disabled={busy}>
              Salva
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function KBMetadataSchemaPage() {
  const { toast } = useToast();
  const { user } = useAuth();
  const canEdit = user?.role === 'admin' || user?.role === 'super_admin';
  const [options, setOptions] = useState<KbMetadataOption[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  const load = useCallback(async (): Promise<void> => {
    try {
      const res = await apiClient.get<KbMetadataOption[]>('/kb-metadata-schema');
      if (!res.success) throw new Error(res.error || 'Errore caricamento');
      setOptions(res.data ?? []);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Errore';
      toast({ title: 'Errore', description: msg, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const byDim = (dim: KbDimension): KbMetadataOption[] => options.filter((o) => o.dimension === dim);

  return (
    <div className="w-full p-6 space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Schema metadata Knowledge Base</h1>
        <p className="text-muted-foreground mt-1">
          Configura i valori disponibili per Lingue, Audience e Sensitività dei documenti caricati
          nella Knowledge Base degli agenti AI di questo tenant.
        </p>
        {!canEdit && (
          <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
            <Eye className="w-4 h-4" />
            Sola lettura: la modifica dello schema è riservata agli amministratori.
          </p>
        )}
      </div>

      {loading ? (
        <p className="text-muted-foreground">Caricamento…</p>
      ) : (
        <div className="space-y-4">
          <DimensionEditor dimension="language" options={byDim('language')} canEdit={canEdit} onMutate={load} />
          <DimensionEditor dimension="audience" options={byDim('audience')} canEdit={canEdit} onMutate={load} />
          <DimensionEditor dimension="sensitivity" options={byDim('sensitivity')} canEdit={canEdit} onMutate={load} />
        </div>
      )}
    </div>
  );
}
